import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";

const PgRouting = () => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal States
  const [showOperatorModal, setShowOperatorModal] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState("");
  const [operatorStatus, setOperatorStatus] = useState(true);

  // Response Modal States
  const [operatorResponse, setOperatorResponse] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);

  const { callApi: getOperators } = useApi(
    "/admin/operator",
    "GET"
  );

  const { callApi: updateOperator } = useApi(
    "/admin/operator/toggle",
    "POST"
  );

  // ================= FETCH =================
  const fetchOperators = async () => {
    setLoading(true);
    try {
      const res = await getOperators();
      console.log("Operators:", res);

      // Filter for PG / CHECKOUT operators
      const filtered = res?.filter(
        (op) => op.paymentMode === "CHECKOUT"
      );

      setOperators(filtered || []);
    } catch (err) {
      console.error("Operator API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOperators();
  }, []);

  // ================= HANDLE DROPDOWN CHANGE =================
  // This fixes the bug: It syncs the toggle with the actual current status of the selected operator.
  const handleOperatorSelect = (e) => {
    const val = e.target.value;
    setSelectedOperator(val);

    const matchingOp = operators.find((op) => op.operator === val || op.displayName === val);
    if (matchingOp) {
      setOperatorStatus(matchingOp.isEnabled);
    } else {
      setOperatorStatus(true); // default if nothing is selected
    }
  };

  // ================= UPDATE LOGIC =================
  const handleOperatorUpdate = async () => {
    if (!selectedOperator) {
      alert("Select Operator");
      return;
    }

    try {
      // Calls: /admin/operator/toggle?operator=FINKEDA&paymentMode=CHECKOUT&enabled=true/false
      const res = await updateOperator(
        null,
        `?operator=${selectedOperator}&enabled=${operatorStatus}&paymentMode=CHECKOUT`
      );

      // ✅ SAVE RESPONSE & SHOW SUCCESS
      setOperatorResponse(res);
      setShowResponseModal(true);

      // Reset modal state
      setShowOperatorModal(false);
      setSelectedOperator("");
      setOperatorStatus(true);

    } catch (err) {
      console.error(err);
      setOperatorResponse({
        message: "Something went wrong",
        isEnabled: false,
      });
      setShowResponseModal(true);
    } finally {
      fetchOperators();
    }
  };

  // ================= UI =================
  return (
    <div className="p-6">
      
      {/* TOP CARDS: PG OPERATORS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {loading ? (
          <div className="col-span-full p-4">Loading operators...</div>
        ) : operators.length === 0 ? (
          <div className="col-span-full text-gray-500 bg-gray-50 p-4 rounded-xl border">
            No PG Operators found.
          </div>
        ) : (
          operators.map((op) => (
            <div
              key={op.id}
              className={`rounded-xl p-4 shadow-sm border 
                ${
                  op.isEnabled
                    ? "bg-gradient-to-r from-green-100 to-green-50 border-green-200"
                    : "bg-gradient-to-r from-red-100 to-red-50 border-red-200"
                }`}
            >
              <p className="text-sm text-gray-500">PG Operator</p>

              <h3
                className={`text-lg font-semibold 
                  ${op.isEnabled ? "text-green-700" : "text-red-700"}`}
              >
                {op.displayName || op.operator}
              </h3>

              <span
                className={`inline-block mt-2 px-2 py-1 text-xs rounded
                  ${
                    op.isEnabled
                      ? "bg-green-200 text-green-800"
                      : "bg-red-200 text-red-800"
                  }`}
              >
                {op.isEnabled ? "Active" : "Inactive"}
              </span>
            </div>
          ))
        )}
      </div>

      {/* HEADER & BUTTON */}
      <div className="flex justify-between mb-6">
        <h2 className="text-xl font-semibold text-blue-600">
          PG Routing 
        </h2>

        <div className="flex gap-3">
          <button
            onClick={() => {
              setSelectedOperator("");
              setOperatorStatus(true);
              setShowOperatorModal(true);
            }}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg"
          >
            Operator Handle
          </button>
        </div>
      </div>

      {/* ================= MODALS ================= */}

      {/* OPERATOR HANDLE MODAL */}
      {showOperatorModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-40">
          <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl">
            
            <h3 className="text-lg font-semibold mb-4 text-purple-600">
              Operator Handle
            </h3>

            <div className="space-y-4">
              {/* DROPDOWN - specifically for FINKEDA */}
              <select
                value={selectedOperator}
                onChange={handleOperatorSelect}
                className="w-full border p-2 rounded bg-white"
              >
                <option value="">Select Operator</option>
                <option value="FINKEDA">FINKEDA</option>
              </select>

              {/* TOGGLE */}
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Status</span>

                <button
                  onClick={() => setOperatorStatus(!operatorStatus)}
                  className={`px-4 py-1 rounded-full text-sm ${
                    operatorStatus
                      ? "bg-green-100 text-green-600"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  {operatorStatus ? "Active" : "Inactive"}
                </button>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowOperatorModal(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>

              <button
                onClick={handleOperatorUpdate}
                className="px-4 py-2 bg-purple-600 text-white rounded"
              >
                Update
              </button>
            </div>

          </div>
        </div>
      )}

      {/* RESPONSE/SUCCESS MODAL */}
      {showResponseModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white w-full max-w-sm rounded-xl p-6 text-center shadow-xl">
            
            {/* ICON */}
            <div className="mb-3">
              {operatorResponse?.isEnabled ? (
                <div className="text-green-500 text-3xl">✅</div>
              ) : (
                <div className="text-red-500 text-3xl">⚠️</div>
              )}
            </div>

            {/* TITLE */}
            <h3 className="text-lg font-semibold mb-2">
              {operatorResponse?.isEnabled
                ? "Operator Enabled"
                : "Operator Disabled"}
            </h3>

            {/* MESSAGE */}
            <p className="text-sm text-gray-600 mb-5">
              {operatorResponse?.displayName || operatorResponse?.operator || "FINKEDA"}{" "}
              is now{" "}
              {operatorResponse?.isEnabled ? "ACTIVE" : "INACTIVE"}
            </p>

            {/* OK BUTTON */}
            <button
              onClick={() => setShowResponseModal(false)}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
            >
              OK
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default PgRouting;
